import React, { useState } from "react";
import { productRepo, ORDER_TYPE } from "../../services";
import { priceLines, checkStock, finalizeSale, resolveBranchId } from "../../services/commerce";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import PaymentQrModal from "../../components/PaymentQrModal";
import { Button, PageHead, Qty, useAsync, baht } from "../../components/ui";

export default function Pos() {
  const { orgId, organization, branchId, branches, toast } = useApp();
  const { tx } = useI18n();
  const { data: products } = useAsync(() => productRepo.all(orgId, (row) => row.isActive), [orgId], []);
  const [cart, setCart] = useState([]);
  const [showQr, setShowQr] = useState(false);
  const [quote, setQuote] = useState(null); // บรรทัดที่คิดราคาใหม่จากระบบแล้ว ณ ตอนขึ้น QR
  const [checking, setChecking] = useState(false);
  const [allowNegative, setAllowNegative] = useState(false);
  const saleBranchId = resolveBranchId(branches, branchId);
  const [search, setSearch] = useState("");

  const addItem = (product) => {
    setCart((rows) => {
      const existing = rows.find((row) => row.productId === product.id);
      if (existing) return rows.map((row) => (row.productId === product.id ? { ...row, quantity: row.quantity + 1 } : row));
      return [...rows, { productId: product.id, name: product.name, price: product.price, quantity: 1 }];
    });
  };

  const setQty = (id, value) => setCart((rows) => (value <= 0 ? rows.filter((row) => row.productId !== id) : rows.map((row) => (row.productId === id ? { ...row, quantity: value } : row))));

  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const filtered = (products || []).filter((product) => product.name.toLowerCase().includes(search.toLowerCase()));

  // ขึ้น QR: คิดราคาใหม่จากระบบ + เช็คสต็อกสาขานี้ก่อน (ไม่เชื่อราคา/จำนวนที่ค้างในหน้าจอ)
  const startPay = async () => {
    if (checking) return;
    if (!saleBranchId) return toast(tx("กรุณาเลือกสาขาที่ขายก่อน", "Please select a branch first"), "error");
    setChecking(true);
    try {
      const fresh = await priceLines(orgId, cart.map((item) => ({ productId: item.productId, quantity: item.quantity })));
      if (fresh.missing.length || !fresh.lines.length) {
        setCart((rows) => rows.filter((row) => !fresh.missing.includes(row.productId)));
        return toast(tx("มีสินค้าที่ปิดขายหรือไม่พบในระบบ นำออกจากตะกร้าแล้ว กรุณาตรวจอีกครั้ง", "Some items are unavailable and were removed — please review"), "error");
      }
      const short = await checkStock(orgId, saleBranchId, fresh.lines);
      // หน้าร้าน: สต็อกไม่พอเตือนแล้วให้ยืนยันขายต่อได้ (สต็อกจะติดลบ + stockMove บันทึกตามจริง)
      if (short.length) {
        const list = short.map((row) => `${row.name} (${tx("เหลือ", "left")} ${row.have}, ${tx("ขาย", "sell")} ${row.need})`).join(", ");
        if (!window.confirm(tx(`สต็อกไม่พอ: ${list}\nขายต่อและให้สต็อกติดลบหรือไม่`, `Not enough stock: ${list}\nSell anyway and let stock go negative?`))) return;
      }
      setAllowNegative(short.length > 0);
      if (Math.abs(fresh.total - total) > 0.005) {
        setCart(fresh.lines.map((line) => ({ productId: line.productId, name: line.name, price: line.price, quantity: line.quantity })));
        return toast(tx("ราคาในระบบเปลี่ยนแปลง อัปเดตตะกร้าแล้ว กรุณาตรวจยอดอีกครั้ง", "Prices changed — cart updated, please review the total"), "error");
      }
      setQuote(fresh);
      setShowQr(true);
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setChecking(false);
    }
  };

  const onPaid = async (transaction) => {
    const result = await finalizeSale({ orgId, organization, kind: "pos", lines: quote.lines, branchId: saleBranchId, transaction, allowNegative });
    setCart([]);
    setQuote(null);
    setShowQr(false);
    toast(tx(`บันทึกการขายเรียบร้อย · ใบเสร็จ ${result.receipt.docNo}`, `Sale recorded · receipt ${result.receipt.docNo}`), "success");
    if (result.warnings.length) toast(result.warnings.join(" / "), "error");
  };

  return (
    <>
      <PageHead title={tx("ขายหน้าร้าน (POS)", "Point of sale")} subtitle={tx("เลือกสินค้า แล้วขึ้น QR ให้ลูกค้าสแกนจ่ายได้ทันที", "Pick items then show a QR for the customer to scan")} />

      <div className="pos-layout">
        <div>
          <input className="input-crm u-mb-16" placeholder={tx("ค้นหาสินค้า", "Search product")} value={search} onChange={(e) => setSearch(e.target.value)} />
          <div className="pos-grid">
            {filtered.map((product) => (
              <button key={product.id} type="button" className="pos-item" onClick={() => addItem(product)}>
                <div className="pos-item__name">{product.name}</div>
                <div className="pos-item__price">{baht(product.price, 0)}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="pos-cart card-crm">
          <div className="u-strong u-fs-18 u-mb-16">{tx("ตะกร้า", "Cart")}</div>
          {cart.length === 0 ? (
            <div className="empty-crm">{tx("ยังไม่มีสินค้าในตะกร้า", "Cart is empty")}</div>
          ) : (
            cart.map((item) => (
              <div className="pos-line" key={item.productId}>
                <div style={{ flex: "1 1 auto" }}>
                  <div>{item.name}</div>
                  <div className="u-fs-12 u-muted">{baht(item.price, 0)} x {item.quantity}</div>
                </div>
                <Qty value={item.quantity} onChange={(value) => setQty(item.productId, value)} />
              </div>
            ))
          )}

          <div className="u-between u-mt-16" style={{ fontSize: 20, fontWeight: 800 }}>
            <span>{tx("รวม", "Total")}</span>
            <span>{baht(total)}</span>
          </div>

          <Button variant="primary" block disabled={!cart.length || checking} onClick={startPay} className="u-mt-16">
            {tx("ชำระด้วย QR", "Pay with QR")}
          </Button>
        </div>
      </div>

      <PaymentQrModal
        open={showQr && !!quote}
        title={tx("บิลขายหน้าร้าน", "POS sale")}
        amount={quote?.total || 0}
        orderType={ORDER_TYPE.posSale}
        items={(quote?.lines || []).map((item) => ({ productName: item.name, productCode: item.productId, quantity: item.quantity, price: item.price }))}
        onClose={() => setShowQr(false)}
        onPaid={onPaid}
      />
    </>
  );
}
